/**
 * GTM Event names — strictly aligned to .cursor/context/implementation_ready_reconciled_matrix.csv.
 * Every value here MUST appear in the matrix; do not add legacy/non-spec events.
 */
export const GTM_EVENTS = {
  PAGE_VIEW: 'page_view',
  LOGIN: 'login',
  SIGN_UP: 'sign_up',
  REGISTER: 'register',
  CHECK_COVERAGE_CLICK: 'check_coverage_click',
  VIEW_PACKAGES: 'view_packages',
  SELECT_PLAN_TYPE: 'select_plan_type',
  CALL_ME_BACK_CLICK: 'call_me_back_click',
  BUTTON_CLICK: 'button_click',
  SHOW_MY_INTEREST: 'show_my_interest',
  GENERATE_LEAD: 'generate_lead',
  CANCELLED_LEAD: 'cancelled_lead',
  ADD_TO_CART: 'add_to_cart',
  CONFIRM_INSTALL_DETAILS: 'confirm_install_details',
  CHANGE_ADDRESS_CLICK: 'change_address_click',
  FUNNEL_STEP_BACK: 'funnel_step_back',
  INSTALL_DETAILS_SUBMITTED: 'install_details_submitted',
  VIEW_CART: 'view_cart',
  REMOVE_FROM_CART: 'remove_from_cart',
  BEGIN_CHECKOUT: 'begin_checkout',
  CHECKOUT_VIEW: 'checkout_view',
  EDIT_CART: 'edit_cart',
  ADD_PAYMENT_INFO: 'add_payment_info',
  ADD_PAYMENT_METHOD_CLICK: 'add_payment_method_click',
  CONFIRM_ORDER: 'confirm_order',
  ACCEPT_DEBIT_ORDER_VIEW: 'accept_debit_order_view',
  ACCEPT_DEBIT_ORDER: 'accept_debit_order',
  DECLINE_DEBIT_ORDER: 'decline_debit_order',
  OTP_VIEW: 'otp_view',
  OTP_SUBMIT: 'otp_submit',
  OTP_CANCELLED: 'otp_cancelled',
  EMANDATE_OTP_SUBMIT: 'emandate_otp_submit',
  EMANDATE_OTP_RESEND: 'emandate_otp_resend',
  DEBIT_MANDATE_SUCCESS: 'debit_mandate_success',
  DEBIT_MANDATE_SUBMITTED: 'debit_mandate_submitted',
  COMPLETE_ORDER_LATER: 'complete_order_later',
  DEBIT_MANDATE_FAILED: 'debit_mandate_failed',
  DEBIT_MANDATE_PENDING: 'debit_mandate_pending',
  PAYMENT_CHECK_COMPLETE: 'payment_check_complete',
  PAYMENT_CHECK_FAILED: 'payment_check_failed',
  PURCHASE_FAILED: 'purchase_failed',
  PURCHASE: 'purchase',
  INITIATE_CANCEL_ORDER: 'initiate_cancel_order',
  CANCEL_ORDER: 'cancel_order',
  VETTING_COMPLETE: 'vetting_complete',
  VETTING_PASSED: 'vetting_passed',
  VETTING_REFERRED: 'vetting_referred',
  VETTING_FAILED: 'vetting_failed',
} as const;

export type GtmEventName = typeof GTM_EVENTS[keyof typeof GTM_EVENTS];

/**
 * Canonical dataLayer `event` string values — same strings as {@link GTM_EVENTS}.
 * Use for typed defaults and call sites (e.g. `trackPageView(..., GtmDataLayerEvent.PageView)`).
 */
export enum GtmDataLayerEvent {
  PageView = 'page_view',
  Login = 'login',
  SignUp = 'sign_up',
  Register = 'register',
  CheckCoverageClick = 'check_coverage_click',
  ViewPackages = 'view_packages',
  SelectPlanType = 'select_plan_type',
  CallMeBackClick = 'call_me_back_click',
  ButtonClick = 'button_click',
  ShowMyInterest = 'show_my_interest',
  GenerateLead = 'generate_lead',
  CancelledLead = 'cancelled_lead',
  AddToCart = 'add_to_cart',
  ConfirmInstallDetails = 'confirm_install_details',
  ChangeAddressClick = 'change_address_click',
  FunnelStepBack = 'funnel_step_back',
  InstallDetailsSubmitted = 'install_details_submitted',
  ViewCart = 'view_cart',
  RemoveFromCart = 'remove_from_cart',
  BeginCheckout = 'begin_checkout',
  CheckoutView = 'checkout_view',
  EditCart = 'edit_cart',
  AddPaymentInfo = 'add_payment_info',
  AddPaymentMethodClick = 'add_payment_method_click',
  ConfirmOrder = 'confirm_order',
  AcceptDebitOrderView = 'accept_debit_order_view',
  AcceptDebitOrder = 'accept_debit_order',
  DeclineDebitOrder = 'decline_debit_order',
  OtpView = 'otp_view',
  OtpSubmit = 'otp_submit',
  OtpCancelled = 'otp_cancelled',
  EmandateOtpSubmit = 'emandate_otp_submit',
  EmandateOtpResend = 'emandate_otp_resend',
  DebitMandateSuccess = 'debit_mandate_success',
  DebitMandateSubmitted = 'debit_mandate_submitted',
  CompleteOrderLater = 'complete_order_later',
  DebitMandateFailed = 'debit_mandate_failed',
  DebitMandatePending = 'debit_mandate_pending',
  PaymentCheckComplete = 'payment_check_complete',
  PaymentCheckFailed = 'payment_check_failed',
  PurchaseFailed = 'purchase_failed',
  Purchase = 'purchase',
  InitiateCancelOrder = 'initiate_cancel_order',
  CancelOrder = 'cancel_order',
  VettingComplete = 'vetting_complete',
  VettingPassed = 'vetting_passed',
  VettingReferred = 'vetting_referred',
  VettingFailed = 'vetting_failed',
}

/** Event name accepted by `_push` — matrix literals or {@link GtmDataLayerEvent} (same underlying strings). */
export type GtmPushEventName = GtmEventName | GtmDataLayerEvent;

export interface GtmEcommerceItem {
  item_id: string;
  item_name: string;
  item_brand: string;
  item_category: string;
  item_variant: string;
  price: number;
  quantity: number;
}

/** Slug for GTM product / item ids: camelCase → snake_case, then non-alphanumeric → single underscore. */
function slugifyGtmProductSegment(value: string): string {
  return String(value ?? '')
    .trim()
    .replace(/([a-z\d])([A-Z])/g, '$1_$2')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/_+/g, '_');
}

function formatMbpsForProductId(n: unknown): string {
  if (n == null || n === '') return '';
  const x = Number(n);
  return Number.isFinite(x) ? String(Math.trunc(x)) : '';
}

/**
 * Stable product id for dataLayer / ecommerce `item_id` (and flat `product_id`),
 * aligned with tag spec, e.g. `fibre_package_open_serve_30mbps`.
 *
 * Built from: OfferableSubTypeName, provider, upload Mbps, download Mbps
 * (see product team formula), then slugified.
 */
export function buildGtmProductItemId(pkg: any): string {
  const core = pkg?.CoreOfferable ?? pkg;
  const subtype = String(core?.OfferableSubTypeName ?? '').trim();
  const provider = String(
    pkg?.ProviderName
      ?? pkg?.PackageSupplierOrganisationEntityDescription
      ?? pkg?.SupplierName
      ?? ''
  ).trim();
  const ul = formatMbpsForProductId(core?.MbpsUpload);
  const dl = formatMbpsForProductId(core?.MbpsDownload);
  const speedPart =
    ul !== '' && dl !== ''
      ? `${ul}mbs_${dl}mbs`
      : ul !== ''
        ? `${ul}mbs`
        : dl !== ''
          ? `${dl}mbs`
          : '';
  const parts = [slugifyGtmProductSegment(subtype), slugifyGtmProductSegment(provider), speedPart].filter(Boolean);
  const id = parts.join('_');
  if (id) return id;
  return String(pkg?.PackageEntityID ?? core?.PackageEntityID ?? pkg?.item_id ?? '');
}

/** Build ecommerce item from package or sales item object */
export function buildEcommerceItem(pkg: any, quantity = 1): GtmEcommerceItem {
  const core = pkg?.CoreOfferable ?? pkg;
  const itemVariant = core?.MbpsDownload != null && core?.MbpsUpload != null
    ? `${core.MbpsDownload}Mbps_${core.MbpsUpload}Mbps`
    : pkg?.item_variant ?? '';
  const price = core?.Billing_PriceIncl ?? core?.Billing_TotalIncl ?? pkg?.price ?? 0;
  const itemId = buildGtmProductItemId(pkg);
  return {
    item_id: itemId,
    item_name: pkg?.PackageName ?? pkg?.OfferableName ?? pkg?.item_name ?? '',
    item_brand: pkg?.PackageSupplierOrganisationEntityDescription ?? pkg?.SupplierName ?? pkg?.item_brand ?? '',
    item_category: core?.CategoryName ?? pkg?.item_category ?? 'Fibre Packages',
    item_variant: itemVariant,
    price: typeof price === 'number' ? price : 0,
    quantity,
  };
}

/** Build discount_information string from package or sales item */
export function buildDiscountInfo(pkg: any): string {
  const c = pkg?.CoreOfferable ?? pkg;
  if (!c?.Billing_TempDiscountEnabled) return 'N/A';
  const amount = c.Billing_TempDiscountAmount;
  const type = c.Billing_TempDiscountTypeCode === 'Percentage' ? '%' : ' Rand';
  const duration = c.Billing_TempDiscountDuration;
  const durationType = c.Billing_TempDiscount_DurationType ?? '';
  return `${amount}${type} for ${duration} ${durationType}`;
}
